import React, { useEffect, useMemo, useState } from 'react';
import { Play, Plus, Save } from 'lucide-react';
import { Badge, Modal } from '../Scheduler/utils';
import {
  buildRecurringTemplatePayload,
  createEmptyRecurringTemplateForm,
  createRecurringTemplate,
  generateRecurringTemplate,
  listRecurringTemplates,
  recurringTemplateToFormState,
  updateRecurringTemplate,
} from './gigApi';

const DAY_LABELS = [
  { key: 'sunday', label: 'Sun' },
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
];

function sortTemplates(items = []) {
  return [...items].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.name.localeCompare(b.name) || a.id - b.id;
  });
}

export default function GigRecurringTemplateModal({
  open,
  siteId,
  onClose,
  onGenerated,
  onMessage,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => createEmptyRecurringTemplateForm({ siteId }));

  const resolvedSiteId = Number(siteId || form.siteId) || null;
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === Number(form.id)) || null,
    [form.id, templates]
  );

  useEffect(() => {
    if (!open) return;
    setForm((current) => {
      if (current.id) return current;
      return createEmptyRecurringTemplateForm({ siteId });
    });
  }, [open, siteId]);

  useEffect(() => {
    if (!open || !resolvedSiteId) {
      if (!resolvedSiteId) {
        setTemplates([]);
      }
      return;
    }

    let cancelled = false;

    async function loadTemplates() {
      try {
        setLoading(true);
        setError('');
        const items = await listRecurringTemplates({ siteId: resolvedSiteId });
        if (cancelled) return;
        setTemplates(sortTemplates(items));
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load recurring templates');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [open, resolvedSiteId]);

  const updateForm = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const toggleDay = (dayKey) => {
    setForm((current) => {
      const currentDays = Array.isArray(current.daysOfWeek) ? current.daysOfWeek : [];
      const nextDays = currentDays.includes(dayKey)
        ? currentDays.filter((entry) => entry !== dayKey)
        : [...currentDays, dayKey];
      return {
        ...current,
        daysOfWeek: nextDays,
      };
    });
  };

  const handleNewTemplate = () => {
    setError('');
    setForm(createEmptyRecurringTemplateForm({ siteId: resolvedSiteId }));
  };

  const handleSave = async () => {
    try {
      setBusy(true);
      setError('');
      const payload = buildRecurringTemplatePayload({ ...form, siteId: resolvedSiteId });
      const savedTemplate = form.id
        ? await updateRecurringTemplate(form.id, payload)
        : await createRecurringTemplate(payload);

      setTemplates((current) => sortTemplates([
        ...current.filter((template) => template.id !== savedTemplate.id),
        savedTemplate,
      ]));
      setForm(recurringTemplateToFormState(savedTemplate));
      onMessage?.(form.id ? 'Recurring template saved' : 'Recurring template created');
    } catch (err) {
      setError(err.message || 'Failed to save recurring template');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateNow = async () => {
    if (!form.id) {
      setError('Save the template before generating gigs');
      return;
    }

    try {
      setBusy(true);
      setError('');
      const result = await generateRecurringTemplate(form.id);
      await onGenerated?.();
      onMessage?.(
        result?.createdCount
          ? `Generated ${result.createdCount} recurring gig${result.createdCount === 1 ? '' : 's'}`
          : 'No new recurring gigs were needed'
      );
    } catch (err) {
      setError(err.message || 'Failed to generate recurring gigs');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Recurring Gig Templates">
      <div className="grid gap-4 xl:grid-cols-[0.9fr,1.1fr]">
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-white">Templates</div>
              <div className="text-xs text-slate-400">Reuse repeating gig schedules without recreating them manually.</div>
            </div>
            <button
              type="button"
              onClick={handleNewTemplate}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs text-white hover:bg-white/20"
            >
              <Plus className="h-4 w-4" />
              New
            </button>
          </div>

          <div className="mt-4 grid gap-2 max-h-[420px] overflow-auto pr-1">
            {loading ? (
              <div className="text-sm text-slate-400">Loading templates…</div>
            ) : templates.length ? (
              templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => {
                    setError('');
                    setForm(recurringTemplateToFormState(template));
                  }}
                  className={`rounded-2xl px-3 py-3 text-left ring-1 ${
                    selectedTemplate?.id === template.id
                      ? 'bg-white/10 ring-white/30'
                      : 'bg-black/20 ring-white/10 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate text-sm font-semibold text-white">{template.name}</div>
                    <Badge className={template.active ? 'bg-emerald-500/20 text-emerald-100' : 'bg-slate-500/20 text-slate-200'}>
                      {template.active ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">
                    {template.daysOfWeek.join(', ')} • {template.startTimeLocal.slice(0, 5)}-{template.endTimeLocal.slice(0, 5)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Generates {template.generateDaysAhead} days ahead
                  </div>
                </button>
              ))
            ) : (
              <div className="text-sm text-slate-400">No recurring templates yet.</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-white">
                {form.id ? 'Edit Template' : 'New Template'}
              </div>
              <div className="text-xs text-slate-400">
                Save a repeating gig pattern, then generate any missing occurrences on demand or via cron.
              </div>
            </div>
            {form.id ? (
              <Badge className={form.active ? 'bg-emerald-500/20 text-emerald-100' : 'bg-slate-500/20 text-slate-200'}>
                {form.active ? 'Active' : 'Paused'}
              </Badge>
            ) : null}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="grid gap-1 md:col-span-2">
              <span className="text-xs text-slate-300">Template Name</span>
              <input
                type="text"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.name}
                onChange={(event) => updateForm({ name: event.target.value })}
                placeholder="Sunset Sessions"
              />
            </label>

            <label className="grid gap-1 md:col-span-2">
              <span className="text-xs text-slate-300">Default Description</span>
              <textarea
                rows={3}
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.defaultDescription}
                onChange={(event) => updateForm({ defaultDescription: event.target.value })}
                placeholder="Beachside house session every Friday"
              />
            </label>

            <div className="grid gap-2 md:col-span-2">
              <span className="text-xs text-slate-300">Repeats On</span>
              <div className="flex flex-wrap gap-2">
                {DAY_LABELS.map((day) => {
                  const selected = form.daysOfWeek.includes(day.key);
                  return (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => toggleDay(day.key)}
                      className={`rounded-xl px-3 py-2 text-xs ring-1 ${
                        selected
                          ? 'bg-indigo-600/90 ring-indigo-300/40 text-white'
                          : 'bg-black/20 ring-white/10 text-slate-300 hover:bg-white/5'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-slate-300">Start Time</span>
              <input
                type="time"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.startTimeLocal}
                onChange={(event) => updateForm({ startTimeLocal: event.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-slate-300">End Time</span>
              <input
                type="time"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.endTimeLocal}
                onChange={(event) => updateForm({ endTimeLocal: event.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-slate-300">Timezone</span>
              <input
                type="text"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.timezoneName}
                onChange={(event) => updateForm({ timezoneName: event.target.value })}
                placeholder="Asia/Ho_Chi_Minh"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-slate-300">UTC Offset Snapshot</span>
              <input
                type="number"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.utcOffsetMinutesSnapshot}
                onChange={(event) => updateForm({ utcOffsetMinutesSnapshot: event.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-slate-300">Generate Days Ahead</span>
              <input
                type="number"
                min="1"
                max="365"
                className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                value={form.generateDaysAhead}
                onChange={(event) => updateForm({ generateDaysAhead: event.target.value })}
              />
            </label>

            <label className="flex items-center gap-3 rounded-xl bg-black/20 px-3 py-2 ring-1 ring-white/10">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(event) => updateForm({ active: event.target.checked })}
              />
              <span className="text-sm text-slate-200">Active template</span>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !resolvedSiteId}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600/90 px-3 py-2 text-sm text-white hover:bg-emerald-600 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              Save Template
            </button>
            <button
              type="button"
              onClick={handleGenerateNow}
              disabled={busy || !form.id}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-600/90 px-3 py-2 text-sm text-white hover:bg-sky-600 disabled:opacity-60"
            >
              <Play className="h-4 w-4" />
              Generate Now
            </button>
          </div>

          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-700/50 bg-rose-900/20 px-3 py-2 text-sm text-rose-200">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
